import React, { useState } from 'react';
import { Button } from "@/components/ui/button";
import { Check, Clock, Eye, ChevronDown, ChevronUp, Pen } from "lucide-react";
import { Tooltip } from "@/components/ui/tooltip";

interface TemplateVersion {
  id: string;
  createdAt: string;
  previewUrl?: string;
}

interface TemplateVersionBannerProps {
  templateId: string;
  latestVersion?: TemplateVersion;
  previousVersions?: TemplateVersion[];
  onOpenVersion: (versionId: string) => void;
  onEditTemplate: () => void;
  suggestions?: string[];
  onApplySuggestion?: (suggestion: string) => void;
}

const TemplateVersionBanner: React.FC<TemplateVersionBannerProps> = ({
  templateId,
  latestVersion,
  previousVersions = [],
  onOpenVersion,
  onEditTemplate,
  suggestions = [],
  onApplySuggestion
}) => {
  const [expanded, setExpanded] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(true);

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleString(undefined, {
        day: '2-digit',
        month: '2-digit',
        year: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (e) {
      return dateString;
    }
  };

  const handleVersionClick = (versionId: string) => {
    onOpenVersion(versionId);
    setExpanded(false);
  };

  return (
    <div className="bg-gray-800/60 border border-jade/20 rounded-lg p-3 my-4 shadow-lg">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="bg-jade/20 p-1.5 rounded-full">
            <Check className="h-4 w-4 text-jade" />
          </div>
          <span className="font-medium text-white">E-Mail-Template {latestVersion ? 'erstellt' : 'generiert'}</span>
          {latestVersion && (
            <span className="text-gray-400 text-xs ml-1">
              {formatDate(latestVersion.createdAt)}
            </span>
          )}
        </div>

        <div className="flex items-center gap-2">
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => onOpenVersion(latestVersion?.id || templateId)}
            className="text-jade hover:text-white hover:bg-jade/20"
          >
            <Eye className="h-3.5 w-3.5 mr-1" />
            Ansehen
          </Button>
          
          <Button 
            variant="ghost" 
            size="sm"
            onClick={onEditTemplate}
            className="text-jade hover:text-white hover:bg-jade/20"
          >
            <Pen className="h-3.5 w-3.5 mr-1" />
            Bearbeiten
          </Button>
          
          {previousVersions.length > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setExpanded(!expanded)}
              className="text-gray-400 hover:text-white"
            >
              {expanded ? 
                <ChevronUp className="h-4 w-4" /> : 
                <ChevronDown className="h-4 w-4" />
              }
            </Button>
          )}
        </div>
      </div>

      {expanded && previousVersions.length > 0 && (
        <div className="mt-3 pt-3 border-t border-gray-700">
          <h4 className="text-sm font-medium text-gray-300 mb-2">Frühere Versionen</h4>
          <div className="space-y-2">
            {previousVersions.map((version) => (
              <div 
                key={version.id}
                className="flex items-center justify-between p-2 rounded hover:bg-gray-700/50 cursor-pointer"
                onClick={() => handleVersionClick(version.id)}
              >
                <div className="flex items-center gap-2">
                  <Clock className="h-3.5 w-3.5 text-gray-400" />
                  <span className="text-sm text-gray-300">Version vom {formatDate(version.createdAt)}</span>
                </div>
                <Button variant="ghost" size="sm" className="text-xs text-gray-400 hover:text-jade">
                  <Eye className="h-3 w-3 mr-1" />
                  Ansehen
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}

      {showSuggestions && suggestions.length > 0 && (
        <div className="mt-3 pt-3 border-t border-gray-700 animate-fade-in">
          <h4 className="text-sm font-medium text-jade mb-2">Verbesserungsvorschläge</h4>
          <div className="space-y-2">
            {suggestions.map((suggestion, index) => (
              <div key={index} className="flex items-start justify-between p-2 rounded bg-gray-700/20">
                <p className="text-sm text-gray-300">{suggestion}</p>
                {onApplySuggestion && (
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="text-xs border-jade text-jade hover:bg-jade/20 hover:text-white ml-2"
                    onClick={() => onApplySuggestion(suggestion)}
                  >
                    Anwenden
                  </Button>
                )}
              </div>
            ))}
            <div className="flex justify-end">
              <Button 
                variant="ghost" 
                size="sm" 
                className="text-xs text-gray-400 hover:text-white"
                onClick={() => setShowSuggestions(false)}
              >
                Verbergen
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TemplateVersionBanner;
